// @LTG-ID: data/brief-01
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 08_FEATURES.md#F-050, source `Journals/what-is-my-business-worth.html`
// @CHANGED: 2026-02 session_004 (CHG-0059)
// @OWNER: web
//
// Non-React supporting data for Brief 01 — frontmatter, JSON-LD payload,
// At-a-Glance rows, sources, end-CTA copy, and read-next cards. React body
// (h2/p/lists/figures/callouts/quotes/table) lives inline in
// `pages/Brief01.jsx` per ORCH-0050 A4 (JSX-inline content, no MDX).

export const BRIEF_01_SLUG = "what-is-my-business-worth";

export const BRIEF_01_FRONTMATTER = {
    number: "01",
    section: "Core Valuation",
    sectionHref: "/journals",
    sectionAnchor: "core-valuation",
    title:
        "What is my business actually worth? An owner's primer on business valuation.",
    deck: "A defensible answer requires three things: a normalized earnings number, a defensible multiple drawn from market evidence, and an honest reading of the company's risk profile. This is how a CPA walks an owner through that process.",
    author: "Sara F. Gonzalez, CPA",
    authorRole: "Managing Partner, KGOB",
    authorInitials: "SG",
    publishedISO: "2026-05-23",
    publishedHuman: "May 23, 2026",
    readMinutes: 14,
    lastReviewed: "2026-08-27",
    ctaId: "brief-01-order",
    ctaLabel: "Get Your Valuation",
    pageTitle:
        "What is my business actually worth? A valuation primer · LTG",
    canonical:
        "https://letstalkgrowth.com/journals/what-is-my-business-worth",
    jsonLd: {
        "@context": "https://schema.org",
        "@type": "Article",
        headline:
            "What is my business actually worth? An owner's primer on business valuation",
        description:
            "A CPA's plain-English guide to what your business is actually worth, the three approaches buyers use, and where most owners get it wrong.",
        author: {
            "@type": "Person",
            name: "Sara F. Gonzalez",
            jobTitle: "Managing Partner, CPA",
            url: "https://kgob.com",
        },
        publisher: {
            "@type": "Organization",
            name: "KGOB CPAs & Advisors",
            logo: {
                "@type": "ImageObject",
                url: "https://kgob.com/logo.png",
            },
        },
        datePublished: "2026-05-23",
        dateModified: "2026-05-23",
        mainEntityOfPage:
            "https://letstalkgrowth.com/journals/what-is-my-business-worth",
    },
};

// At-a-Glance dl rows. `dd` is HTML so <strong> markers in source survive.
export const BRIEF_01_AT_A_GLANCE = [
    {
        dt: "Target reader",
        dd:
            "Founder-owner, age 55 to 68, $1M to $25M in revenue, has never had a formal valuation.",
    },
    {
        dt: "Key statistic 1",
        dd:
            "<strong>80%+</strong> of owners' net worth sits in the business; only <strong>27%</strong> of Baby Boomer owners have a formal valuation (EPI 2023 National State of Owner Readiness Report).",
    },
    {
        dt: "Key statistic 2",
        dd:
            "Median small-business sale price <strong>$350,000</strong>; median cash flow multiple <strong>2.61x</strong>; median revenue multiple <strong>0.69x</strong> (BizBuySell 2025 Year-End Insight Report).",
    },
    {
        dt: "Key statistic 3",
        dd:
            "Among credentialed appraisers, recast EBITDA is the most-used multiples method, applied in <strong>76%</strong> of valuation engagements (Pepperdine Private Capital Markets Report 2025).",
    },
    {
        dt: "Last reviewed",
        dd:
            "March 2026 &middot; Refreshed quarterly off IBBA Market Pulse and BizBuySell Insight releases.",
    },
];

export const BRIEF_01_SOURCES = [
    {
        id: "s1",
        html:
            "Exit Planning Institute, <em>2023 National State of Owner Readiness Report</em>. Available with email at exit-planning-institute.org.",
    },
    {
        id: "s2",
        html:
            "BizBuySell, <em>2025 Year-End Insight Report</em>. Available at bizbuysell.com/insight-report.",
    },
    {
        id: "s3",
        html:
            "Pepperdine Private Capital Markets Report 2025, Dr. Craig R. Everett, Pepperdine Graziadio Business School. Available at digitalcommons.pepperdine.edu.",
    },
    {
        id: "s4",
        html:
            "IBBA &amp; M&amp;A Source Market Pulse Report, Q4 2025. Available at ibba.org.",
    },
    {
        id: "s5",
        html: "AICPA Statement on Standards for Valuation Services No. 1 (SSVS No. 1).",
    },
    { id: "s6", html: "IRS Revenue Ruling 59-60." },
    {
        id: "s7",
        html:
            "FOCUS Investment Banking, &ldquo;Customer Concentration in Lower-Middle-Market M&amp;A,&rdquo; July 2025.",
    },
    {
        id: "s8",
        html: "CT Acquisitions, Manufacturing Industry Valuation Report, 2026.",
    },
    { id: "s9", html: "GF Data Resources, Q1 2025 quarterly report." },
];

export const BRIEF_01_END_CTA = {
    label: "Your business, your number",
    headlineLead: "Get the indicative valuation,",
    headlineEm: "signed by a CPA",
    headlineTrail: ".",
    body:
        "The Strategic Snapshot applies the methodology behind this article to your business. Indicative valuation range, ten-lever scorecard, three priority moves, and a tax-efficiency review.",
    secondaryHref: "/#snapshot",
    secondaryLabel: "Try the free preview →",
    microLine:
        "Indicative valuation, not a formal appraisal under SSVS No. 1",
};

// Read-next rail. Brief 02 lands in P3-M3 — the link still points at the
// /journals/sde-vs-ebitda route which currently resolves to JournalNotFound
// (showing Brief 01 in its "did you mean..." panel). After P3-M3, the link
// reaches the published Brief 02. The other two cards point at section anchors
// on the Journals index, which lands in P3-M4.
export const BRIEF_01_READ_NEXT = [
    {
        tag: "Brief 02 · Core Valuation",
        title:
            "SDE vs EBITDA: which multiple actually applies to your business.",
        meta: "12 min read · Forthcoming",
        to: "/journals/sde-vs-ebitda",
    },
    {
        tag: "Brief 03 · Core Valuation",
        title: "The three approaches to value: income, market, asset.",
        meta: "11 min read · Forthcoming",
        to: "/journals#brief-3",
    },
    {
        tag: "Brief 06 · Core Valuation",
        title: "DLOM and minority interest discounts, explained.",
        meta: "15 min read · Forthcoming",
        to: "/journals#brief-6",
    },
];
